<template>
  <div id="monitoring-ship-maneuvering">
    <vs-row vs-w="12">
      <vs-col
        vs-type="flex"
        vs-justify="center"
        vs-align="center"
        vs-lg="9"
        vs-sm="4"
        vs-xs="12"
      >
        <div id="container-main">
          <see-route/>
          <live-map ref="liveMap" @closeLoading="closeLoading"/>
          <alert-summary ref="child" @initialiseAlertHistory="initialiseAlertHistory"/>
        </div>
      </vs-col>
      <vs-col vs-type="flex" vs-lg="3" vs-sm="4" vs-xs="12">
        <div id="container-vessel-data">
          <div id="item-vessel-name">
            <vs-row class="mt-6">
              <vessel-select/>
              <vessel-communication-channel/>
            </vs-row>
          </div>

          <div id="item-vessel-status">
            <vessel-status/>
          </div>
          <div id="item-vessel-data">
            <tabs-maneuvering-data/>
          </div>
        </div>
      </vs-col>
    </vs-row>

    <DialogSelectVessel ref="dialogSelectVessel"/>
  </div>
</template>

<script>
import vSelect from "vue-select";
import SecureDataflowService from "@/services/secureDataflowService";
import moment from "moment";

// Mixin
import {
  StoreCommonsMixin,
  StoreMenuMixin,
  StoreMonitoringVesselManeuveringMixin
} from "@/mixins/addon/secure-dataflow";

// components
import SeeRoute from "./components/sea-route/SeaRoute";
import DialogSelectVessel from "./components/dialogs/DialogSelectVessel";
import LiveMap from "./components/live-map/LiveMap";
import AlertSummary from "./components/alert-summary/AlertSummary";
import VesselSelect from "./components/VesselSelect";
import VesselStatus from "./components/VesselStatus";
import VesselCommunicationChannel from "../components/VesselCommunicationChannel";
import TabsManeuveringData from "./components/tabs-maneuvering-data/TabsManeuveringData";
import {db} from "@/firebase/firebaseConfig.js";

import {createNamespacedHelpers} from "vuex"
const { mapGetters, mapActions, mapState } = createNamespacedHelpers("monitoringVesselEngine");

export default {
  name: "monitoringVesselManeuvering",
  mixins: [
    StoreCommonsMixin,
    StoreMenuMixin,
    StoreMonitoringVesselManeuveringMixin
  ],
  components: {
    vSelect,
    SeeRoute,
    DialogSelectVessel,
    LiveMap,
    AlertSummary,
    VesselStatus,
    VesselSelect,
    VesselCommunicationChannel,
    TabsManeuveringData
  },
  data() {
    return {
      vessel: null,
      storeUnsubscribe: null
    };
  },
  watch: {
    vesselInfo(val, old) {
      this.loadDate();
    },
    async isIdle() {
      let r = this.$router.resolve({
        name: "login-enterprise",
        query: {logout: "force"}
      });
      window.location.assign(r.href)
    }
  },
  computed: {
    ...mapGetters(["getAlertSummaryData", "getVersion"]),
    ...mapState({
      alertOccurTimeMap: state => state.alertOccurTimeMap
    }),
    isIdle() {
      return this.$store.state.idleVue.isIdle;
    }
  },
  methods: {
    ...mapActions([
      "setModeState",
      "setChannelTimeFromState",
      "setChannelTimeToState",
      "setChannelsDataSearchedByStatus",
      "addAlertOccurTime",
      "resetAlertOccurTime",
      "setNotFoundAlertOccurTime"
    ]),
     /**
     * 過去24時間分のエラー発生日時を検索
     */
    async initialiseAlertHistory() {
      let time_from = null;
      let time_to = null;

      let liveDataTimeRange = 60 * 24;
      time_from = moment().subtract(liveDataTimeRange, "minutes").toISOString();
      time_to   = moment().toISOString();

      await this.setChannelTimeFromState(time_from);
      await this.setChannelTimeToState(  time_to);

      this.$nextTick(async () => {
        await this.setModeState("live");
        await this.searchAlertEventHistory();
      });
    },
    /**
     * アラート発生時のデータを取得
     */
    async searchAlertEventHistory() {
      // チャンネルタイムは残す、発生時間にチャンネルタイムを使う
      let alertChannelList = this.$refs.child.alertList.map(element => {
        return {
          channel_id: element.channelId,
          channel_time: element.localTime
        }
      }).filter(element => element.channel_id != null)
      if (alertChannelList.length == 0) return // アラート状態のチャンネルがない場合何もしない
      
      let payload = {
        status_contains: ["E"],
        channel_group: "alert",
        channel_ids: alertChannelList.map(element => element.channel_id)
      }
      await this.setChannelsDataSearchedByStatus(payload);

      // APIから取得したアラートサマリーチャンネルデータを時間順にソート
      // findで一番最初に見つけたチャンネルデータを最新のデータとし、発生時間マップに登録
      let data = [...this.getAlertSummaryData]
      let sortedAlertSummaryData = await data.sort(function (a, b) {
        return moment(b.channel_time).valueOf() - moment(a.channel_time).valueOf()
      })

      alertChannelList.forEach(element => {
        let latestData = sortedAlertSummaryData.find(target => {
          return target.channel_id == element.channel_id
        })
        // APIから取得したデータに該当のチャンネルデータがなければ、
        // firestoreのチャンネルデータを発生時間として発生時間マップに登録
        if (latestData) {
          this.addAlertOccurTime(latestData)
          // 最新時間が見つかったアラートをリストから除外
          alertChannelList = alertChannelList.filter(alert => alert.channel_id != latestData.channel_id)
        }
      })

      // リストから除外されていないアラートを対象に再度時間を広げて検索を行う
      if (alertChannelList.length > 0) { // 全ての発生時間が見つからなかった場合
        let liveDataTimeRangeFrom = 60 * 24 * 7;
        let liveDataTimeRangeTo = 60 * 24;
        const time_from = moment().subtract(liveDataTimeRangeFrom, "minutes").toISOString();
        const time_to   = moment().subtract(liveDataTimeRangeTo, "minutes").toISOString();

        await this.setChannelTimeFromState(time_from);
        await this.setChannelTimeToState(  time_to);

        payload = {
          status_contains: ["E"],
          channel_group: "alert",
          channel_ids: alertChannelList.map(element => element.channel_id)
        }
        await this.setChannelsDataSearchedByStatus(payload);

        // APIから取得したアラートサマリーチャンネルデータを時間順にソート
        // findで一番最初に見つけたチャンネルデータを最新のデータとし、発生時間マップに登録
        data = [...this.getAlertSummaryData]
        sortedAlertSummaryData = await data.sort(function (a, b) {
          return moment(b.channel_time).valueOf() - moment(a.channel_time).valueOf()
        })

        alertChannelList.forEach(element => {
          let latestData = sortedAlertSummaryData.find(target => {
            return target.channel_id == element.channel_id
          })
          // APIから取得したデータに該当のチャンネルデータがなければ、
          // firestoreのチャンネルデータを発生時間として発生時間マップに登録
          if (latestData) {
            this.addAlertOccurTime(latestData)
          } else {
            this.setNotFoundAlertOccurTime(element)
          }
        })
      }
    },

    async loadDate() {
      this.$vs.loading()
      await this.UNSUBSCRIBE_FIRE_STORE();
      await this.resetStateOfMonitoringVesselManeuvering();

      await this.setTrackHistoryPositions({
        vesselId: this.vesselInfo.id,
        trackHistoryInterval: this.trackHistoryInterval
      });

      await this.subscribeSeaRoute({
        vesselId: this.vesselInfo.id,
        originId: 3,
        requestId: "none"
      });

      this.subscribeEvent({
        vesselId: this.vesselInfo.id,
        originId: 2,
        requestId: "none"
      });

      this.subscribeChannelDataManeuvering({
        vesselId: this.vesselInfo.id,
        versionName: this.maneuveringVersion.name
      });

      this.setInitialFOFlow({
        vesselId: this.vesselInfo.id,
        originId: 1,
        kind: "engine",
        versionId: this.engineVersion.id,
        versionName: this.engineVersion.name
      })

      this.subscribeChannelDataEngine({
        vesselId: this.vesselInfo.id,
        versionName: this.engineVersion.name
      });

      this.subscribeChannelDataAlert({
        vesselId: this.vesselInfo.id,
        versionName: this.alertVersion.name
      });

    },
    closeLoading() {
      this.$vs.loading.close()
    }
  },
  mounted() {
    this.$store.commit("UPDATE_MAIN_LAYOUT_TYPE", "secureDataflow");
    let routerViewClass = document.getElementsByClassName("router-view");
    routerViewClass[0].style.padding = "0.0rem";
  },
  async created() {
    let vessel = await SecureDataflowService.getVessel();
    if (typeof vessel === "undefined") {
      this.SET_SHOW_DIALOG_SELECT_VESSEL(true);
    } else {
      let payload = {
        vesselInfo: vessel.vessel_info,
        maneuveringOriginId: vessel.maneuvering_origin_id,
        engineOriginId: vessel.engine_origin_id,
        alertOriginId: vessel.alert_origin_id
      };
      await this.setVesselInfoAndVersions(payload);
      await this.setChannelMaster({
        vesselId: vessel.vessel_info.id,
        params: {
          version_id: this.engineVersion.id
        }
      });
      await this.setOwnVessels()

      //setTimeout(() => {this.initialiseAlertHistory()}, 6000);
    }
  },
  beforeDestroy() {
    this.UNSUBSCRIBE_FIRE_STORE();
    this.resetStateOfMonitoringVesselManeuvering();
    this.resetAlertOccurTime();
  }
};
</script>

<style lang="scss">
@import "@/assets/scss/secure-dataflow/monitoringVesselManeuvering.scss";
</style>
